import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { inputBase } from '@/styles/mixins';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const Label = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text};
  letter-spacing: 0.01em;
`;

const InputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const IconWrapper = styled.div<{ position: 'left' | 'right' }>`
  position: absolute;
  ${({ position }) => position === 'left' ? 'left: 14px;' : 'right: 14px;'}
  color: ${({ theme }) => theme.colors.textTertiary};
  display: flex;
  align-items: center;
  pointer-events: none;
  transition: color ${({ theme }) => theme.transitions.fast};
`;

const StyledInput = styled.input<{ hasLeftIcon?: boolean; hasRightIcon?: boolean; hasError?: boolean }>`
  ${inputBase}
  height: 44px;
  font-size: 16px; /* Prevent zoom on iOS */
  
  ${({ hasLeftIcon }) => hasLeftIcon && css`
    padding-left: 44px;
  `}
  
  ${({ hasRightIcon }) => hasRightIcon && css`
    padding-right: 44px;
  `}
  
  ${({ hasError, theme }) => hasError && css`
    border-color: ${theme.colors.error};
    
    &:focus {
      border-color: ${theme.colors.error};
      box-shadow: 0 0 0 4px ${theme.colors.errorLight};
    }
  `}
  
  &:hover:not(:focus):not(:disabled) {
    border-color: ${({ theme }) => theme.colors.borderDark};
  }
  
  @media (max-width: 480px) {
    height: 48px;
  }
`;

const HintText = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ErrorText = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  color: ${({ theme }) => theme.colors.error};
`;

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, hint, leftIcon, rightIcon, ...props }, ref) => {
    return (
      <InputWrapper>
        {label && <Label>{label}</Label>}
        <InputContainer>
          {leftIcon && <IconWrapper position="left">{leftIcon}</IconWrapper>}
          <StyledInput
            ref={ref}
            hasLeftIcon={!!leftIcon}
            hasRightIcon={!!rightIcon}
            hasError={!!error}
            {...props}
          />
          {rightIcon && <IconWrapper position="right">{rightIcon}</IconWrapper>}
        </InputContainer>
        {hint && !error && <HintText>{hint}</HintText>}
        {error && <ErrorText>{error}</ErrorText>}
      </InputWrapper>
    );
  }
);

Input.displayName = 'Input';

